/* eslint-disable no-loop-func */
import { agentPageTaskPrompt, agentPageTaskObservation, type AgentPageTaskResult } from './page-task-protocol';
import type {
  AgentPageDecision,
  AgentPageElement,
  AgentPageObservation,
  AgentPageScope,
  AgentPageTaskPort,
} from './page-task-protocol';
import type { createPagePointer } from './page-pointer';
import { readAgentPageContent, AGENT_PAGE_EXCLUDE } from './page-content';
import { readAgentPageSemantics } from './page-semantics';

const TARGET = 'data-fett-page-target';
const CONTROLS =
  'button,a[href],input,textarea,[role="button"],[role="tab"],[role="combobox"],[role="option"],[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"],[role="checkbox"],[role="radio"],[role="switch"],[data-agent-action="open"]';
const FORM = '[data-agent-form], [data-account-form], [role="dialog"], [role="alertdialog"], form';
const EXCLUDE = '[data-agent-page-exclude], [data-agent-chat], [data-message-id]';

/** 仅向查询输入开放 Enter，防止触发业务表单提交。 */
function isSearchInput(element: HTMLElement): element is HTMLInputElement {
  return element instanceof HTMLInputElement &&
    !element.closest('form:not([role="search"])') &&
    (element.type === 'search' ||
      element.getAttribute('role') === 'searchbox' ||
      element.getAttribute('data-agent-purpose') === 'search' ||
      (element.type === 'text' && Boolean(element.closest('[role="search"]'))));
}

function label(element: HTMLElement): string {
  const labelledBy = element
    .getAttribute('aria-labelledby')
    ?.split(/\s+/)
    .map((id) => element.ownerDocument.getElementById(id)?.textContent ?? '')
    .join(' ');
  const labels =
    'labels' in element
      ? Array.from((element as HTMLInputElement).labels ?? [])
          .map((item) => item.textContent)
          .join(' ')
      : '';
  return (
    element.getAttribute('aria-label') ||
    labelledBy ||
    labels ||
    (element.id
      ? element.ownerDocument.querySelector(`label[for="${CSS.escape(element.id)}"]`)?.textContent
      : '') ||
    element.closest('label')?.textContent ||
    element.closest('[data-slot="field"]')?.querySelector('[data-slot="field-label"]')
      ?.textContent ||
    element.getAttribute('placeholder') ||
    readAgentPageContent('workspace', element, 1000).text.replace(/\[p\d+\] /g, '').replace(/\s+/g, ' ') ||
    ''
  )
    .trim()
    .slice(0, 240);
}
function allowed(element: HTMLElement, scope: AgentPageScope, popups: Set<HTMLElement>): boolean {
  if (
    !element.isConnected ||
    Boolean(element.closest('[data-agent-effect="commit"]')) ||
    element.closest(EXCLUDE) ||
    element.closest('[hidden],[inert],[aria-hidden="true"],[aria-disabled="true"]') ||
    element.matches(':disabled,[readonly]') ||
    !element.getClientRects().length
  )
    return false;
  if (
    scope === 'form' &&
    !element.closest(FORM) &&
    !Array.from(popups).some((popup) => popup.contains(element))
  )
    return false;
  const style = getComputedStyle(element);
  return style.visibility !== 'hidden' && style.display !== 'none';
}
function actions(element: HTMLElement, scope: AgentPageScope): AgentPageElement['actions'] {
  if (element.hasAttribute('data-fett-page-scroll')) return ['scroll'];
  if (
    scope === 'workspace' &&
    element.matches('[role="switch"],[role="checkbox"],[role="radio"],[aria-pressed],input[type="checkbox"],input[type="radio"]') &&
    !element.closest(FORM) &&
    !element.closest('[data-agent-safe-choice]')
  )
    return [];
  if (
    element.matches(
      'input:not([type="hidden"]):not([type="file"]):not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]),textarea',
    )
  )
    return isSearchInput(element) ? ['fill', 'press'] : ['fill'];
  if (
    element.matches(
      '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"],[role="option"],[role="combobox"],[role="tab"],[role="checkbox"],[role="radio"],[role="switch"],[aria-pressed],input[type="checkbox"],input[type="radio"]',
    )
  )
    return ['click'];
  if (element.matches('a[href]')) {
    const href = new URL((element as HTMLAnchorElement).href, window.location.href);
    return scope === 'workspace' &&
      href.origin === window.location.origin &&
      element.hasAttribute('data-agent-nav')
      ? ['click']
      : [];
  }
  // 查询区域的提交仅做检索；业务提交仍留给用户。
  const searchLandmarkButton = element.matches('[role="search"] button:not([type="reset"])');
  if (
    element.matches('[data-agent-action="close"],[data-agent-action="read"],[data-slot="error-state-retry"]') &&
    !element.matches('[type="submit"]')
  )
    return ['click'];
  if (
    (element.matches('[type="submit"]') && !searchLandmarkButton) ||
    element.closest('footer,[data-slot="dialog-footer"],[data-slot="sheet-footer"]')
  )
    return [];
  if (
    searchLandmarkButton ||
    (element.matches('button[data-search-button],button[data-agent-purpose="search"]') &&
      (element.getAttribute('type') === 'button' || !element.closest('form'))) ||
    (element.matches('button[data-page-btn],button[data-page-first],button[data-page-prev],button[data-page-next],button[data-page-last]') &&
      Boolean(element.closest('[data-page-info]')) &&
      (element.getAttribute('type') === 'button' || !element.closest('form')))
  )
    return ['click'];
  if (
    element.hasAttribute('aria-haspopup') ||
    element.hasAttribute('aria-expanded') ||
    element.matches(
      '[data-agent-nav],[data-agent-nav-expand],[data-agent-action="open"],[data-slot="dialog-trigger"],[data-slot="sheet-trigger"],[data-header-action="create"]',
    )
  )
    return ['click'];
  // 未登记的普通按钮和菜单项交由 page-agent 观察、选择；提交边界仍由执行器拦截。
  if (element.matches('button,[role="button"],[role="menuitem"],input[type="button"]'))
    return ['click'];
  if (
    element
      .getAttributeNames()
      .some((name) => /^(data-create-|data-add-|data-open-|data-edit-)/.test(name))
  )
    return ['click'];
  return [];
}

const PAGE_LAYER = '[data-slot="popover-content"],[data-slot="select-content"],[data-slot="dropdown-menu-content"],[role="dialog"],[role="alertdialog"],[role="listbox"],[role="menu"]';

/** 浮层必须先处理或关闭，不能用 DOM click 绕过它操作背景页面。 */
function insideActiveLayer(element: HTMLElement, popups: Set<HTMLElement>): boolean {
  const layers = Array.from(document.querySelectorAll<HTMLElement>(PAGE_LAYER)).filter(
    (layer) =>
      !layer.closest('[data-closed],[data-state="closed"]') &&
      allowed(layer, 'workspace', new Set()) &&
      // 同一浮层内部的 listbox/menu 不是新的弹出层，不能遮掉它自己的搜索框。
      !layer.parentElement?.closest(PAGE_LAYER),
  );
  const layer = layers.at(-1);
  if (!layer || layer.contains(element)) return true;
  // 表单控件可能把所属选项 portal 到 body；只承认本任务已打开且由当前层控制的面板。
  for (const trigger of layer.querySelectorAll('[aria-controls]')) {
    for (const id of (trigger.getAttribute('aria-controls') ?? '').split(/\s+/)) {
      const popup = document.getElementById(id);
      if (popup && popups.has(popup) && popup.contains(element)) return true;
    }
  }
  // Popover 可以再次点击所属触发器关闭；保留这条退出路径，不放开其它背景控件。
  return Boolean(
    layer.id &&
    element.getAttribute('aria-expanded') === 'true' &&
    element.getAttribute('aria-controls')?.split(/\s+/).includes(layer.id),
  );
}

const IDENTITY_ATTR = /^data-(?!agent(?:-|$)|fett(?:-|$)|perseus(?:-|$)|active$|state$|closed$|open$|slot(?:-|$)|icon(?:-|$))/;

/** 控件自身或最近祖先上的业务数据属性；不绑定具体字段名。 */
function identityAttributes(element: HTMLElement): Attr[] {
  for (let node: HTMLElement | null = element; node && node !== document.body; node = node.parentElement) {
    const attributes = [...node.attributes].filter(
      (attr) => IDENTITY_ATTR.test(attr.name) && attr.value,
    );
    if (attributes.length) return attributes;
  }
  return [];
}

/** 区分同名入口指向的不同记录。 */
function ownerKey(element: HTMLElement): string {
  return identityAttributes(element)
    .map((attr) => `${attr.name}=${attr.value}`)
    .sort()
    .join('|');
}

/**
 * 同类候选：属性名相同、标签相同。
 * 换下一个候选只在同一类里换，不会从会话列表跳到菜单或别的面板。
 */
function familyKey(element: HTMLElement): string {
  return [
    element.tagName,
    ...identityAttributes(element)
      .map((attr) => attr.name)
      .sort(),
  ].join('|');
}

/** 跨快照记住已点开的候选；编号每轮重排，不能拿来判断是不是同一个入口。 */
function controlSignature(element: HTMLElement): string {
  const action = element.getAttribute('data-agent-action') ?? '';
  const owner = ownerKey(element);
  // 列表会因新消息重排；有稳定标识时不掺位置，否则同一条记录换个位置就被当成新候选。
  let position = '';
  if (!owner) {
    const peers = document.querySelectorAll(`[data-agent-action="${CSS.escape(action)}"]`);
    peers.forEach((peer, index) => {
      if (peer === element) position = String(index);
    });
  }
  return [
    action,
    owner,
    label(element),
    element.getAttribute('href') ?? '',
    element.getAttribute('aria-controls') ?? '',
    position,
  ].join('\u0000');
}

function isCurrentSelection(element: HTMLElement): boolean {
  const current = element.getAttribute('aria-current');
  const active = element.getAttribute('data-active');
  return Boolean(current && current !== 'false') || active === '' || active === 'true';
}

function searchCanContinue(element: HTMLElement, visited: Set<string>): boolean {
  // 详情层会给自身或背景打上 hidden / inert / aria-hidden，不能因此把关闭和下一个入口排除掉。
  return (
    element.isConnected &&
    !element.closest(AGENT_PAGE_EXCLUDE) &&
    !element.closest('[data-agent-effect="commit"]') &&
    !element.closest('[hidden]') &&
    element.getClientRects().length > 0 &&
    !visited.has(controlSignature(element))
  );
}

/**
 * 模型把「当前候选不匹配」当成整次查找结束。
 * 已经打开过候选时：先关详情，再打开同类里下一个还没看过的入口。没有这类入口才接受结束。
 */
function nextLiveSearchTarget(visited: Set<string>, family: string | undefined): HTMLElement | undefined {
  if (!family) return undefined;
  const close = [...document.querySelectorAll<HTMLElement>('[data-agent-action="close"]')].find(
    (element) => searchCanContinue(element, visited),
  );
  if (close) return close;
  return [...document.querySelectorAll<HTMLElement>('[data-agent-action="open"]')].find(
    (element) =>
      familyKey(element) === family &&
      searchCanContinue(element, visited) &&
      !isCurrentSelection(element),
  );
}

/** 导航的选中态独立于页面快照；列表加载和控件重排不能重新开放当前入口。 */
function isCurrentNavigation(element: HTMLElement): boolean {
  return element.matches('a[href][data-agent-nav]') && isCurrentSelection(element);
}

/** 等待业务 DOM 短暂稳定；忽略 Agent 状态/指针动画，不给快速模型固定追加长延迟。 */
function settlePage(scope: AgentPageScope, cancelled: () => boolean): Promise<void> {
  return new Promise((resolve) => {
    const started = performance.now();
    let changed = started;
    const relevant = (node: Node) => {
      const element = node instanceof Element ? node : node.parentElement;
      return Boolean(
        element &&
        !element.closest(EXCLUDE) &&
        !element.closest('#playwright-highlight-container') &&
        (scope === 'workspace' || element.closest(FORM)),
      );
    };
    const observer = new MutationObserver((records) => {
      if (records.some((record) => relevant(record.target))) changed = performance.now();
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-busy', 'aria-selected', 'aria-expanded', 'aria-checked', 'hidden'],
    });
    const timer = setInterval(() => {
      const now = performance.now();
      const busy = Array.from(document.querySelectorAll('[aria-busy="true"]')).some(
        (element) => relevant(element) && element.getClientRects().length > 0,
      );
      if (cancelled() || now - started >= 1500 || (!busy && now - changed >= 120)) {
        clearInterval(timer);
        observer.disconnect();
        resolve();
      }
    }, 40);
  });
}

/** page-agent 负责识别；动作经壳的指针执行，范围和提交限制不交给模型决定。 */
export function createAgentPageTask(options: {
  pointer: ReturnType<typeof createPagePointer>;
  ready: () => Promise<boolean>;
  track: (event: string, data: unknown) => void;
}): AgentPageTaskPort {
  let running = false;
  let cancelActive: (() => void) | undefined;
  let lastResult: AgentPageTaskResult | undefined;
  return {
    cancel: () => cancelActive?.(),
    getResult: (taskId) => lastResult?.actionId === taskId ? lastResult : undefined,
    async run(task, scope, decide, signal, onProgress) {
      if (running) return 'blocked';
      if (signal.aborted) {
        lastResult = { actionId: task.id, status: 'cancelled', reason: 'interrupted', completedSteps: 0, modelCalls: 0, durationMs: 0 };
        return 'cancelled';
      }
      running = true;
      if (task.handoff?.scope === 'form') scope = 'form';
      const prompt = agentPageTaskPrompt(task);
      let cancelled: boolean = signal.aborted;
      let userInterrupted = false;
      let rejectPending: (() => void) | undefined;
      const waitPending = async <T>(promise: Promise<T>): Promise<T> => {
        if (cancelled) throw new Error('AGENT_PAGE_TASK_CANCELLED');
        let interrupt!: () => void;
        const stopped = new Promise<never>((_resolve, reject) => {
          interrupt = () => reject(new Error('AGENT_PAGE_TASK_CANCELLED'));
          rejectPending = interrupt;
        });
        try {
          return await Promise.race([promise, stopped]);
        } finally {
          if (rejectPending === interrupt) rejectPending = undefined;
        }
      };
      const cancel = () => {
        cancelled = true;
        rejectPending?.();
        options.pointer.port.cancel();
      };
      cancelActive = cancel;
      const onInput = (event: Event) => {
        if (!event.isTrusted) return;
        // 查看执行记录不会干预业务页面，不中止自动执行。
        if (event.target instanceof Element && event.target.closest('[data-agent-page-progress]'))
          return;
        if (event.type === 'keydown') {
          const key = event as KeyboardEvent;
          if (
            (!key.key || key.key === 'Unidentified') &&
            !key.code &&
            !key.keyCode &&
            !key.isComposing
          )
            return;
        }
        userInterrupted = true;
        cancel();
      };
      const onHidden = () => {
        if (document.visibilityState === 'hidden') cancel();
      };
      signal.addEventListener('abort', cancel);
      document.addEventListener('pointerdown', onInput, true);
      document.addEventListener('keydown', onInput, true);
      document.addEventListener('visibilitychange', onHidden);
      const tagged = new Set<HTMLElement>();
      const popups = new Set<HTMLElement>();
      const filled = new Map<HTMLElement, string>();
      let layerBypass: HTMLElement | undefined;
      const initialScope = scope;
      let recordFormLocked = false;
      const inScope = (element: HTMLElement) =>
        allowed(element, scope, popups) &&
        (element === layerBypass || insideActiveLayer(element, popups));
      let previous: AgentPageObservation['previous'];
      let focusedClickTarget: HTMLElement | undefined;
      const clickedStates = new Map<string, Set<string>>();
      const scrolledStates = new Map<string, Set<'up' | 'down'>>();
      let rejectedScrolls = 0;
      const visitedControls = new Set<string>();
      let inspectedFamily: string | undefined;
      const history: NonNullable<AgentPageObservation['history']> = [];
      let outcome: AgentPageTaskResult['status'] = 'failed';
      let reason: AgentPageTaskResult['reason'] = 'execution_failed';
      let completedSteps = 0;
      let firstActionMs: number | undefined;
      let lastState: string | undefined;
      let unchanged = 0;
      let timedOut = false;
      let phase = 'load';
      let observeMs = 0;
      let modelMs = 0;
      let modelCalls = 0;
      let decisionRetries = 0;
      let actionMs = 0;
      const startedAt = performance.now();
      // 预算是进度检查窗口，不是整个查找的硬上限；续执行保留同一任务的所有现场。
      const maxSteps = task.handoff?.budget.maxSteps ?? 40;
      const timeoutMs = task.handoff?.budget.timeoutMs ?? 120000;
      const seenStates = new Set<string>();
      let progressCount = 0;
      let stepProgress = 0;
      let timeProgress = 0;
      let nextStepCheckpoint = maxSteps;
      let wasLoading = false;
      let loadingSince: number | undefined;
      const trackContinuation = (trigger: 'steps' | 'time') => {
        options.track('agent_page_task_budget_renewed', {
          actionId: task.id,
          trigger,
          progressCount,
          completedSteps,
          modelCalls,
          durationMs: Math.round(performance.now() - startedAt),
        });
      };
      const checkTimeProgress = () => {
        if (cancelled) return;
        if (progressCount > timeProgress) {
          timeProgress = progressCount;
          trackContinuation('time');
          budgetTimer = setTimeout(checkTimeProgress, timeoutMs);
          return;
        }
        timedOut = true;
        cancel();
      };
      let budgetTimer = setTimeout(checkTimeProgress, timeoutMs);
      try {
        for (let step = 0; ; step++) {
          layerBypass = undefined;
          if (cancelled) return (outcome = timedOut ? 'blocked' : 'cancelled');
          if (recordFormLocked && !document.querySelector('[data-agent-form]')) {
            scope = initialScope;
            recordFormLocked = false;
          }
          onProgress?.({ step: step + 1, action: 'observe', status: 'running' });
          phase = 'mount';
          if (!(await waitPending(options.ready()))) return (outcome = 'failed');
          if (scope === 'form') {
            const deadline = Date.now() + 15000;
            while (!document.querySelector(FORM) && !cancelled && Date.now() < deadline)
              await new Promise((resolve) => setTimeout(resolve, 100));
          }
          if (cancelled) return (outcome = timedOut ? 'blocked' : 'cancelled');
          phase = 'observe';
          const observeStartedAt = performance.now();
          const revision = crypto.randomUUID();
          for (const element of tagged) {
            element.removeAttribute(TARGET);
            element.removeAttribute('data-fett-page-scroll');
          }
          tagged.clear();
          const candidates = new Map<string, HTMLElement>();
          for (const element of document.querySelectorAll<HTMLElement>(CONTROLS)) {
            if (!inScope(element) || !actions(element, scope).length) continue;
            const id = `p${candidates.size}`;
            element.setAttribute(TARGET, id);
            tagged.add(element);
            candidates.set(id, element);
          }
          // 真实滚动容器是通用查询入口；滚动触发现有分页/懒加载，不引入业务专用请求。
          const scrollTargets: HTMLElement[] = [];
          const scrollCandidates = [
            document.scrollingElement,
            ...document.querySelectorAll<HTMLElement>('body *'),
          ];
          for (const candidate of scrollCandidates) {
            if (!(candidate instanceof HTMLElement)) continue;
            const element = candidate;
            if (scrollTargets.length >= 20) break;
            if (
              !inScope(element) ||
              element.closest(AGENT_PAGE_EXCLUDE) ||
              element.hasAttribute(TARGET)
            )
              continue;
            if (
              element.scrollHeight <= element.clientHeight + 1 ||
              element.clientHeight <= 0 ||
              (element !== document.scrollingElement &&
                !/(auto|scroll)/.test(getComputedStyle(element).overflowY))
            )
              continue;
            element.setAttribute('data-fett-page-scroll', '');
            const id = `p${candidates.size}`;
            element.setAttribute(TARGET, id);
            tagged.add(element);
            candidates.set(id, element);
            scrollTargets.push(element);
          }
          // 直接从实时 DOM 提取可见控件；无需等待未用于决策的浏览器快照。
          // 文本仍独立限量提取，聊天正文与草稿不随页面任务发送。
          const elements: AgentPageElement[] = [];
          for (const [id, element] of candidates) {
            const agentAction = element.getAttribute('data-agent-action');
            if (elements.length >= 500) break;
            const sensitive =
              element.matches('input[type="password"],[data-agent-sensitive]') ||
              /password|secret|token|api.?key/i.test(
                [
                  element.getAttribute('name'),
                  element.getAttribute('autocomplete'),
                  element.closest('[data-agent-field]')?.getAttribute('data-agent-field'),
                ]
                  .filter(Boolean)
                  .join(' '),
              );
            elements.push({
              id,
              label: scrollTargets.includes(element)
                ? element.getAttribute('aria-label')?.slice(0, 240) || 'Scrollable area'
                : label(element),
              kind:
                element.getAttribute('role') ||
                element.getAttribute('type') ||
                element.tagName.toLowerCase(),
              actions: actions(element, scope),
              sensitive,
              semantics: sensitive ? undefined : readAgentPageSemantics(element),
              ...(element.dataset.agentRoute
                ? { value: element.dataset.agentRoute }
                : {}),
              ...(element.matches('a[href][data-agent-nav]')
                ? { value: new URL((element as HTMLAnchorElement).href, window.location.href).pathname }
                : {}),
              ...(agentAction === 'open' || agentAction === 'close' || agentAction === 'read'
                ? { agentAction }
                : {}),
              ...(scrollTargets.includes(element)
                ? {
                    value: `${element.scrollTop}/${Math.max(0, element.scrollHeight - element.clientHeight)}`,
                  }
                : {}),
              ...(element.matches('input,textarea,[role="combobox"]')
                ? {
                    value: sensitive
                      ? (element as HTMLInputElement).value
                        ? filled.get(element) === (element as HTMLInputElement).value
                          ? '[filled]'
                          : '[existing]'
                        : ''
                      : element.matches('[role="combobox"]:not(input)')
                        ? (element.getAttribute('aria-valuetext') || element.textContent || '')
                            .trim()
                            .slice(0, 2000)
                        : String((element as HTMLInputElement).value).slice(0, 2000),
                  }
                : {}),
              ...(element.hasAttribute('aria-checked') ||
              element.hasAttribute('aria-pressed') ||
              element.hasAttribute('aria-selected')
                ? {
                    checked:
                      (element.getAttribute('aria-checked') ??
                        element.getAttribute('aria-pressed') ??
                        element.getAttribute('aria-selected')) === 'true',
                  }
                : element.matches('input[type="checkbox"],input[type="radio"]')
                  ? { checked: (element as HTMLInputElement).checked }
                  : {}),
            });
            if (JSON.stringify(elements).length > 45000) {
              elements.pop();
              break;
            }
          }
          const content = readAgentPageContent(scope);
          observeMs += performance.now() - observeStartedAt;
          // 以语义快照判重，不用每轮变化的 revision；列表重渲染不会清空访问记录。
          const state = JSON.stringify({
            path: window.location.pathname + window.location.search,
            content: content.text.replace(/\[p\d+\] /g, ''),
            elements: elements.map((item) => ({
              ...item,
              id: undefined,
              expanded: candidates.get(item.id)?.getAttribute('aria-expanded'),
            })),
          });
          // 方向按同一快照和滚动容器去重，A→B→A 不能再次走 A→B。
          // 在新内容或筛选结果下重新允许滚动，不把容器永久禁用。
          const scrollKeys = new Map<string, string>();
          for (const description of elements) {
            const element = candidates.get(description.id)!;
            const index = scrollTargets.indexOf(element);
            if (index < 0) continue;
            const key = JSON.stringify([state, index]);
            scrollKeys.set(description.id, key);
            const tried = scrolledStates.get(key);
            description.scrollDirections = [];
            if (element.scrollTop > 0 && !tried?.has('up')) description.scrollDirections.push('up');
            if (element.scrollTop < element.scrollHeight - element.clientHeight && !tried?.has('down'))
              description.scrollDirections.push('down');
            if (!description.scrollDirections.length) description.actions = [];
          }
          const clicked = clickedStates.get(state);
          const clickKeys = new Map<string, string>();
          let suppressedClicks = 0;
          for (const description of elements) {
            const element = candidates.get(description.id)!;
            const key = JSON.stringify([
              description.id,
              description.label,
              description.kind,
              element.getAttribute('href'),
              element.getAttribute('aria-controls'),
            ]);
            clickKeys.set(description.id, key);
            if (
              description.actions.includes('click') &&
              (clicked?.has(key) ||
                // 打开过的候选不再提供给模型，避免在两个候选之间来回点击。
                (description.agentAction === 'open' &&
                  visitedControls.has(controlSignature(element))) ||
                isCurrentNavigation(element) ||
                element.matches('[role="tab"][aria-selected="true"]'))
            ) {
              description.actions = description.actions.filter((action) => action !== 'click');
              suppressedClicks++;
            }
          }
          if (suppressedClicks)
            options.track('agent_page_task_clicks_suppressed', {
              actionId: task.id,
              step,
              count: suppressedClicks,
            });
          // 短暂加载和空白不消耗模型调用；可操作内容已出现时忽略持久的全局加载标记。
          const loading = Array.from(
            document.querySelectorAll<HTMLElement>(
              '[aria-busy="true"],[role="progressbar"],[data-slot="skeleton"]',
            ),
          ).some((element) => inScope(element) && !element.closest(AGENT_PAGE_EXCLUDE));
          if (loading) loadingSince ??= performance.now();
          else loadingSince = undefined;
          const blank = !elements.length && !content.text;
          // 业务页已可操作时，不让一个持续存在的全局 loading 指示器永久挡住页面任务。
          if (blank || (loading && performance.now() - loadingSince! < 1500)) {
            wasLoading = true;
            onProgress?.({ step: step + 1, action: 'wait', status: 'running' });
            await waitPending(new Promise((resolve) => setTimeout(resolve, 250)));
            previous = { action: 'wait', status: 'applied' };
            step--;
            continue;
          }
          // 首次快照和重复状态不算进展。只有执行后的新状态或加载出的新内容才可续预算，
          // 单纯等待、重复点击、来回滚动不能无限续期；快照只留内存，不写日志。
          if (!seenStates.has(state)) {
            if (
              wasLoading ||
              (seenStates.size > 0 && previous?.status === 'applied' && previous.action !== 'wait')
            )
              progressCount++;
            seenStates.add(state);
          }
          wasLoading = false;
          if (step >= nextStepCheckpoint) {
            if (progressCount === stepProgress) {
              reason = 'no_progress';
              return (outcome = 'blocked');
            }
            stepProgress = progressCount;
            nextStepCheckpoint = step + maxSteps;
            trackContinuation('steps');
          }
          unchanged = state === lastState ? unchanged + 1 : 0;
          lastState = state;
          // 只有真的打开过某一类候选，才知道该在哪一类里继续换。
          const inspected = inspectedFamily;
          const adopt = (target: HTMLElement) => {
            layerBypass = target;
            try {
              target.scrollIntoView({ block: 'nearest' });
            } catch {
              // jsdom 没有滚动实现时不影响继续查找。
            }
            for (const [id, candidate] of candidates) {
              if (candidate !== target) continue;
              const description = elements.find((item) => item.id === id);
              if (description && !description.actions.includes('click'))
                description.actions = [...description.actions, 'click'];
              return id;
            }
            const id = `c${candidates.size}`;
            target.setAttribute(TARGET, id);
            tagged.add(target);
            candidates.set(id, target);
            elements.push({
              id,
              label: label(target).slice(0, 240),
              kind: target.tagName.toLowerCase(),
              actions: ['click'],
              semantics: readAgentPageSemantics(target),
              agentAction:
                target.getAttribute('data-agent-action') === 'close'
                  ? 'close'
                  : target.getAttribute('data-agent-action') === 'open'
                    ? 'open'
                    : undefined,
            });
            clickKeys.set(
              id,
              JSON.stringify([
                id,
                label(target),
                target.tagName.toLowerCase(),
                target.getAttribute('href'),
                target.getAttribute('aria-controls'),
              ]),
            );
            return id;
          };
          let forcedTarget: HTMLElement | undefined;
          if (unchanged >= 2 && previous?.action !== 'wait') {
            forcedTarget = scope === 'workspace' ? nextLiveSearchTarget(visitedControls, inspected) : undefined;
            if (!forcedTarget) {
              reason = 'no_progress';
              return (outcome = 'blocked');
            }
            unchanged = 0;
          }
          phase = 'decide';
          let decision: AgentPageDecision;
          if (forcedTarget) {
            decision = { action: 'click', target: adopt(forcedTarget), revision };
            options.track('agent_page_task_continue', { actionId: task.id, step, reason: 'no_progress' });
          } else {
            onProgress?.({ step: step + 1, action: 'decide', status: 'running' });
            const modelStartedAt = performance.now();
            modelCalls += 1;
            try {
              decision = await waitPending(
                decide(agentPageTaskObservation(task, { revision, scope, elements, content, history: [...history], previous, ...(prompt.handoff ? { handoff: prompt.handoff } : {}) })),
              ).finally(() => {
                modelMs += performance.now() - modelStartedAt;
              });
            } catch (error) {
              const status = (error as { data?: { status?: number } } | null)?.data?.status;
              if (cancelled || decisionRetries >= 2 || ![502, 503, 504].includes(status ?? 0)) throw error;
              decisionRetries++;
              previous = { action: 'decide', status: 'retry' };
              unchanged = 0;
              options.track('agent_page_task_decision_retry', { actionId: task.id, attempt: decisionRetries, status });
              onProgress?.({ step: step + 1, action: 'wait', status: 'running' });
              await waitPending(new Promise((resolve) => setTimeout(resolve, 500 * decisionRetries)));
              continue;
            }
            if (cancelled) return (outcome = timedOut ? 'blocked' : 'cancelled');
            if (decision.revision !== revision) return (outcome = 'failed');
            if (decision.action === 'done') { reason = 'verified'; return (outcome = 'completed'); }
            if (decision.action === 'blocked' && scope === 'workspace') {
              const target = nextLiveSearchTarget(visitedControls, inspected);
              if (target) {
                decision = { action: 'click', target: adopt(target), revision };
                options.track('agent_page_task_continue', { actionId: task.id, step });
              }
            }
            if (decision.action === 'blocked') { reason = 'model_blocked'; return (outcome = 'blocked'); }
          }
          if (decision.action === 'wait') {
            onProgress?.({ step: step + 1, action: 'wait', status: 'running' });
            await waitPending(new Promise((resolve) => setTimeout(resolve, 1000)));
            previous = { action: 'wait', status: 'applied' };
            onProgress?.({ step: step + 1, action: 'wait', status: 'completed' });
            continue;
          }
          if (
            decision.action !== 'click' &&
            decision.action !== 'fill' &&
            decision.action !== 'press' &&
            decision.action !== 'scroll'
          ) {
            return (outcome = 'failed');
          }
          if (decision.action === 'scroll') {
            const { target, direction } = decision;
            const directions = elements.find((item) => item.id === target)?.scrollDirections;
            if (directions && !directions.includes(direction)) {
              rejectedScrolls++;
              previous = { action: 'scroll', status: 'rejected_direction' };
              options.track('agent_page_task_scroll_rejected', { actionId: task.id, step, direction, attempts: rejectedScrolls });
              if (rejectedScrolls >= 2) { reason = 'no_progress'; return (outcome = 'blocked'); }
              continue;
            }
          }
          rejectedScrolls = 0;
          if (decision.action === 'fill' && ((decision.value !== undefined) === (decision.valueRef !== undefined)))
            return (outcome = 'failed');
          const fillRef = decision.action === 'fill' ? decision.valueRef : undefined;
          const fillValue = decision.action === 'fill'
            ? fillRef !== undefined
              ? task.handoff?.inputs?.find((input) => input.key === fillRef)?.value
              : decision.value
            : undefined;
          if (decision.action === 'fill' && typeof fillValue !== 'string') return (outcome = 'failed');
          const targetFits = (id: string, action: 'click' | 'fill' | 'press' | 'scroll') => {
            const node = candidates.get(id);
            return Boolean(
              node &&
                !node.closest('[data-agent-effect="commit"]') &&
                elements.find((item) => item.id === id)?.actions.includes(action) &&
                actions(node, scope).includes(action) &&
                (layerBypass === node
                  ? node.isConnected && node.getClientRects().length > 0
                  : inScope(node)),
            );
          };
          // 目标失效（被详情层挡住、已重渲染）只说明这一步选错，不代表整次查找结束。
          if (!targetFits(decision.target, decision.action)) {
            const rejected = candidates.get(decision.target);
            if (rejected) visitedControls.add(controlSignature(rejected));
            layerBypass = undefined;
            const next = scope === 'workspace' ? nextLiveSearchTarget(visitedControls, inspected) : undefined;
            if (!next) return (outcome = 'blocked');
            const nextId = adopt(next);
            if (!targetFits(nextId, 'click')) return (outcome = 'blocked');
            decision = { action: 'click', target: nextId, revision };
            options.track('agent_page_task_continue', { actionId: task.id, step, reason: 'target_rejected' });
          }
          const element = candidates.get(decision.target)!;
          const description = elements.find((item) => item.id === decision.target)!;
          if (decision.action === 'press' && (decision.key !== 'Enter' || !isSearchInput(element)))
            return (outcome = 'blocked');
          if (decision.action === 'fill' && element instanceof HTMLInputElement && element.type === 'number') {
            const probe = document.createElement('input');
            probe.type = 'number';
            probe.value = fillValue!;
            if (!fillValue!.trim() || probe.value !== fillValue || !Number.isFinite(probe.valueAsNumber)) {
              previous = { action: 'fill', status: 'invalid_input' };
              options.track('agent_page_task_fill_rejected', {
                actionId: task.id, step, inputType: 'number', reason: 'invalid_value',
              });
              continue;
            }
          }
          if (decision.action === 'fill' &&
              (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) &&
              (element.value === fillValue ||
                (element instanceof HTMLInputElement && element.type === 'number' && element.value !== '' &&
                  Number(element.value) === Number(fillValue)))) {
            previous = { action: 'fill', status: 'already_present' };
            options.track('agent_page_task_fill_skipped', { actionId: task.id, step, reason: 'same_value' });
            continue;
          }
          const clickedSignature = decision.action === 'click' ? controlSignature(element) : undefined;
          if (decision.action === 'click') {
            const seen = clickedStates.get(state) ?? new Set<string>();
            seen.add(clickKeys.get(decision.target)!);
            clickedStates.set(state, seen);
          }
          const scrollBefore = decision.action === 'scroll' ? element.scrollTop : undefined;
          if (decision.action === 'scroll') {
            const key = scrollKeys.get(decision.target)!;
            const tried = scrolledStates.get(key) ?? new Set<'up' | 'down'>();
            tried.add(decision.direction);
            scrolledStates.set(key, tried);
          }
          phase = 'act';
          const progress = {
            step: step + 1,
            action: decision.action,
            label: description.sensitive ? undefined : description.label,
          };
          onProgress?.({ ...progress, status: 'running' });
          let verified = false;
          // 每次只执行一个动作；索引不跨快照复用。
          const actionStartedAt = performance.now();
          const completed = await waitPending(options.pointer.port.point(element, async () => {
            if (cancelled || element.closest('[data-agent-effect="commit"]') || (layerBypass !== element && !inScope(element))) return;
            if (decision.action === 'scroll') {
              const before = element.scrollTop;
              element.scrollTop +=
                (decision.direction === 'up' ? -1 : 1) * Math.max(100, element.clientHeight * 0.8);
              verified = element.scrollTop !== before;
            } else if (decision.action === 'press') {
              element.focus();
              element.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true }));
              element.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true }));
              verified = true;
            } else if (decision.action === 'fill') {
              const input = element as HTMLInputElement | HTMLTextAreaElement;
              input.click();
              input.focus();
              if (
                element.tagName === 'TEXTAREA' ||
                ['text', 'search', 'tel', 'url', 'password'].includes(input.type)
              )
                input.select();
              const prototype =
                element.tagName === 'TEXTAREA'
                  ? HTMLTextAreaElement.prototype
                  : HTMLInputElement.prototype;
              const setValue = Object.getOwnPropertyDescriptor(prototype, 'value')!.set!;
              const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
              const segments = Array.from(
                new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(fillValue!),
                (item) => item.segment,
              );
              const stepSize = Math.max(1, Math.ceil(segments.length / 12));
              const frames =
                reduced ||
                [
                  'number',
                  'date',
                  'time',
                  'datetime-local',
                  'month',
                  'week',
                  'range',
                  'color',
                ].includes(input.type)
                  ? [fillValue!]
                  : Array.from(
                      { length: Math.max(1, Math.ceil(segments.length / stepSize)) },
                      (_, index) => segments.slice(0, (index + 1) * stepSize).join(''),
                    );
              for (const value of frames) {
                if (cancelled || !inScope(element)) return;
                setValue.call(input, value);
                input.dispatchEvent(new Event('input', { bubbles: true }));
                if (!reduced)
                  await new Promise((resolve) =>
                    setTimeout(resolve, Math.min(100, 600 / frames.length)),
                  );
              }
              if (cancelled) return;
              input.dispatchEvent(new Event('change', { bubbles: true }));
              verified = input.value === fillValue;
            } else {
              const href = element.getAttribute('href');
              if (href && element.hasAttribute('data-agent-nav')) {
                options.pointer.expectNavigation(new URL(href, document.baseURI).pathname);
                element.dataset.agentNavigating = 'true';
              }
              try {
                element.focus({ preventScroll: true });
                focusedClickTarget = element;
                element.click();
                verified = true;
              } finally {
                delete element.dataset.agentNavigating;
              }
            }
          }));
          actionMs += performance.now() - actionStartedAt;
          if (!completed || cancelled) return (outcome = timedOut ? 'blocked' : 'cancelled');
          if (decision.action === 'fill') {
            verified = (element as HTMLInputElement).value === fillValue;
            if (verified) filled.set(element, fillValue!);
          }
          await waitPending(settlePage(scope, () => cancelled));
          if (cancelled) return (outcome = timedOut ? 'blocked' : 'cancelled');
          if (scope === 'workspace' && decision.action === 'click' &&
              description.agentAction === 'open' && element.closest('[data-agent-record]') &&
              [...document.querySelectorAll<HTMLElement>('[data-agent-form]')].some((form) =>
                allowed(form, 'workspace', popups))) {
            scope = 'form';
            recordFormLocked = true;
            options.track('agent_page_task_form_locked', { actionId: task.id, step });
          }
          const resultContent = readAgentPageContent(scope);
          for (const id of (element.getAttribute('aria-controls') ?? '').split(/\s+/)) {
            const popup = document.getElementById(id);
            if (popup) popups.add(popup);
          }
          if (verified) {
            completedSteps++;
            firstActionMs ??= Math.round(performance.now() - startedAt);
            if (clickedSignature) visitedControls.add(clickedSignature);
            if (clickedSignature && description.agentAction === 'open')
              inspectedFamily ??= familyKey(element);
          }
          previous = { action: decision.action, status: verified ? 'applied' : 'failed' };
          onProgress?.({ ...progress, status: verified ? 'completed' : 'failed' });
          history.push({
            action: decision.action,
            label: description.label,
            status: previous.status,
            text: `${description.agentAction === 'open' && description.semantics?.fields?.length
              ? `Opened record fields: ${JSON.stringify(description.semantics.fields).slice(0, 280)}\n`
              : ''}${resultContent.text.slice(-700)}`.slice(-1000),
            ...(decision.action === 'scroll' ? {
              direction: decision.direction, scrollBefore, scrollAfter: element.scrollTop,
            } : {}),
          });
          if (history.length > 12) history.shift();
          options.track('agent_page_task_step', {
            actionId: task.id,
            step,
            action: decision.action,
            status: previous.status,
          });
          if (!verified && decision.action !== 'scroll') return (outcome = 'failed');
        }
      } catch {
        return (outcome = timedOut ? 'blocked' : cancelled ? 'cancelled' : 'failed');
      } finally {
        clearTimeout(budgetTimer);
        if (timedOut) { outcome = 'blocked'; reason = 'no_progress'; }
        else if (outcome === 'cancelled') reason = 'interrupted';
        // 页面任务结束时释放由点击留下的焦点，避免悬浮卡片一直展开。
        if (!userInterrupted && focusedClickTarget === document.activeElement)
          focusedClickTarget.blur();
        lastResult = { actionId: task.id, status: outcome, reason, completedSteps, modelCalls,
          durationMs: Math.round(performance.now() - startedAt) };
        options.pointer.expectNavigation();
        for (const element of tagged) {
          element.removeAttribute(TARGET);
          element.removeAttribute('data-fett-page-scroll');
        }
        signal.removeEventListener('abort', cancel);
        document.removeEventListener('pointerdown', onInput, true);
        document.removeEventListener('keydown', onInput, true);
        document.removeEventListener('visibilitychange', onHidden);
        running = false;
        cancelActive = undefined;
        options.track('agent_page_task_settled', {
          actionId: task.id,
          scope,
          status: outcome,
          reason,
          completedSteps,
          firstActionMs,
          phase,
          durationMs: Math.round(performance.now() - startedAt),
          observeMs: Math.round(observeMs),
          modelMs: Math.round(modelMs),
          modelCalls,
          actionMs: Math.round(actionMs),
        });
      }
    },
  };
}
